import type { CampaignPricingMode } from './campaign-pricing.js';
import type { CampaignPeriodStatus } from './campaign-status.js';
import type { Currency } from './currency.js';
import { DONATION_PAYMENT_METHODS, type DonationPaymentMethod } from './donation.js';
import { type EntryLabelOverrides, resolveEntryLabel } from './entry-labels.js';
import type { FormField } from './form-schema.js';

/**
 * A donation import's spreadsheet — docs/decisions/0104 §2. Pure: the template
 * the worker writes and the upload the worker reads are both decided here, so
 * the column a template offers is the column an upload is read by.
 */

/** The raw file's ceiling, the same 5 MiB the upload is presigned for — 0104's limits. */
export const DONATION_IMPORT_MAX_BYTES = 5 * 1024 * 1024;

/** The most rows one import may hold, and so how far down a template's lists reach — 0104's limits. */
export const DONATION_IMPORT_MAX_ROWS = 2_000;

/** Which layout of the template a stamp names; a stamp of any other is not read. */
export const DONATION_IMPORT_TEMPLATE_VERSION = 1;

const STAMP_PREFIX = 'btsis:donation-import';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const STAMP_PATTERN = new RegExp(`^${STAMP_PREFIX}:(\\d{1,4}):(${UUID}):(\\d{1,9})$`, 'i');

export type DonationImportStamp = {
  campaignPeriodId: string;
  formVersion: number;
};

/** What sheet 1's A1 says: which season and which published form the file was drawn from. */
export function importStamp(stamp: DonationImportStamp): string {
  return `${STAMP_PREFIX}:${DONATION_IMPORT_TEMPLATE_VERSION}:${stamp.campaignPeriodId}:${stamp.formVersion}`;
}

/**
 * The stamp back out of a cell, or `null` for anything else — a cell somebody
 * typed over, a stamp of another layout, a sheet that never was a template.
 */
export function readImportStamp(cell: string | null | undefined): DonationImportStamp | null {
  const match = STAMP_PATTERN.exec((cell ?? '').trim());
  if (!match) return null;
  const [, layout, campaignPeriodId, formVersion] = match;
  if (Number(layout) !== DONATION_IMPORT_TEMPLATE_VERSION) return null;
  return {
    campaignPeriodId: (campaignPeriodId as string).toLowerCase(),
    formVersion: Number(formVersion),
  };
}

/**
 * The key of a header cell, `Etiket [key]` → `key`: the last bracketed text at
 * the end of the cell, so a label holding brackets of its own still reads, and
 * a header with no key — a column the clerk added for their own notes — is
 * `null`.
 */
export function headerKeyOf(header: string | null | undefined): string | null {
  const match = /\[([^[\]]*)\]\s*$/.exec(header ?? '');
  const key = match?.[1]?.trim() ?? '';
  return key === '' ? null : key;
}

/** A column's header cell: its Turkish label and, after it, the key it is read by. */
export function importHeader(column: Pick<DonationImportColumn, 'label' | 'key'>): string {
  return `${column.label} [${column.key}]`;
}

export type ImportYurt = { institutionId: string; label: string };

const yurtCollator = new Intl.Collator('tr', { sensitivity: 'base' });

/**
 * The season's yurts by the name a clerk reads — `resolveDisplayName`'s — made
 * unique, because a cell names a yurt by its label alone. Two assignments
 * sharing a name are told apart by a number in parentheses, given in the order
 * of their institution ids, so the same assignments always get the same labels.
 */
export function yurtLabels(
  assignments: readonly { institutionId: string; displayName: string }[],
): ImportYurt[] {
  const ordered = [...assignments].sort(
    (a, b) =>
      yurtCollator.compare(a.displayName.trim(), b.displayName.trim()) ||
      (a.institutionId < b.institutionId ? -1 : a.institutionId > b.institutionId ? 1 : 0),
  );
  const fold = (label: string) => label.trim().toLocaleLowerCase('tr-TR');
  const counts = new Map<string, number>();
  for (const assignment of ordered) {
    const key = fold(assignment.displayName);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const taken = new Set(ordered.map((assignment) => fold(assignment.displayName)));
  const nth = new Map<string, number>();
  return ordered.map((assignment) => {
    const name = assignment.displayName.trim();
    const key = fold(name);
    if ((counts.get(key) ?? 0) < 2) return { institutionId: assignment.institutionId, label: name };
    let index = nth.get(key) ?? 0;
    let label: string;
    do {
      index += 1;
      label = `${name} (${index})`;
    } while (taken.has(fold(label)));
    nth.set(key, index);
    taken.add(fold(label));
    return { institutionId: assignment.institutionId, label };
  });
}

export const DONATION_IMPORT_COLUMN_KINDS = [
  'text',
  'phone',
  'country',
  'quantity',
  'amount',
  'number',
  'date',
  'datetime',
  'choice',
  'multichoice',
  'boolean',
] as const;

export type DonationImportColumnKind = (typeof DONATION_IMPORT_COLUMN_KINDS)[number];

export type DonationImportOption = { value: string; label: string };

export type DonationImportColumn = {
  /** What the header's brackets say. A form field's is `form.<its key>`, so it never meets a fixed one. */
  key: string;
  label: string;
  kind: DonationImportColumnKind;
  /** The labels a cell may hold, for a `choice`, `multichoice` or `boolean` column; otherwise `null`. */
  options: readonly DonationImportOption[] | null;
  required: boolean;
};

/** The prefix a form field's column key carries before the field's own key. */
export const FORM_COLUMN_PREFIX = 'form.';

/** The Turkish a template's fixed columns are headed with — `tr.donationImport` in the catalogue. */
export type DonationImportLabels = {
  parties: { payer: string; owner: string; reference: string };
  partyName: string;
  partyPhone: string;
  payerCountry: string;
  quantity: string;
  amount: string;
  currency: string;
  yurt: string;
  paymentMethod: string;
  paymentReference: string;
  note: string;
  donatedAt: string;
  enteredByUserId: string;
  yes: string;
  no: string;
  paymentMethods: Record<DonationPaymentMethod, string>;
};

export type ImportColumnsInput = {
  form: { fields: readonly FormField[] };
  pricingMode: CampaignPricingMode;
  unitLabel: string | null;
  /** The season's staff currencies, the default first. */
  currencies: readonly Currency[];
  yurts: readonly ImportYurt[];
  entryLabels: EntryLabelOverrides | null;
  /** Whether the requester holds `donation.backdate`. */
  mayBackdate: boolean;
  periodStatus: CampaignPeriodStatus;
  labels: DonationImportLabels;
};

export type ImportColumns =
  | { ok: true; columns: DonationImportColumn[] }
  | { ok: false; refusal: 'DONATION_IMPORT_FORM_NEEDS_FILE'; fieldKey: string };

function column(
  key: string,
  label: string,
  kind: DonationImportColumnKind,
  required = false,
  options: readonly DonationImportOption[] | null = null,
): DonationImportColumn {
  return { key, label, kind, options, required };
}

function fieldColumn(field: FormField, labels: DonationImportLabels): DonationImportColumn {
  const key = `${FORM_COLUMN_PREFIX}${field.key}`;
  const required = field.required && !field.visibleOn;
  switch (field.type) {
    case 'select':
    case 'radio':
      return column(key, field.label, 'choice', required, field.options);
    case 'multiselect':
      return column(key, field.label, 'multichoice', required, field.options);
    case 'checkbox':
      return column(key, field.label, 'boolean', required, [
        { value: 'true', label: labels.yes },
        { value: 'false', label: labels.no },
      ]);
    case 'number':
      return column(key, field.label, 'number', required);
    case 'date':
      return column(key, field.label, 'date', required);
    case 'phone':
      return column(key, field.label, 'phone', required);
    default:
      return column(key, field.label, 'text', required);
  }
}

/**
 * The template's columns, in the order sheet 1 lays them out — 0104 §2: the
 * payer, the quantity or the amount the campaign's pricing asks for, the
 * currency when the season offers more than one, the yurt, the owner (on whose
 * behalf it is given) and the reference under the campaign's own entry labels (0081), the payment
 * method, its reference and the note, the date for a requester who may
 * backdate — **required in a closed season**, because "now" is outside that
 * season's window — and then one column per field of the published form in the
 * admin audience, `adminOnly` included, except `file`, which no cell can carry.
 *
 * A form with a `file` field it always requires cannot be imported at all,
 * since no row could satisfy it; one shown only on a condition is left out and
 * judged row by row. The optional operator UUID overrides the commit default per row; an import
 * tells nobody.
 */
export function importColumns(input: ImportColumnsInput): ImportColumns {
  const neededFile = input.form.fields.find(
    (field) => field.type === 'file' && field.required && !field.visibleOn,
  );
  if (neededFile) {
    return { ok: false, refusal: 'DONATION_IMPORT_FORM_NEEDS_FILE', fieldKey: neededFile.key };
  }

  const { labels } = input;
  const party = (key: 'partyPayer' | 'partyOwner' | 'partyReference', fallback: string) =>
    resolveEntryLabel(input.entryLabels, key, fallback);
  const payer = party('partyPayer', labels.parties.payer);
  const owner = party('partyOwner', labels.parties.owner);
  const reference = party('partyReference', labels.parties.reference);
  const named = (who: string, what: string) => `${who} — ${what}`;

  const columns: DonationImportColumn[] = [
    column('entered_by_user_id', labels.enteredByUserId, 'text'),
    column('payer_name', named(payer, labels.partyName), 'text', true),
    column('payer_phone', named(payer, labels.partyPhone), 'phone', true),
    column('payer_country', named(payer, labels.payerCountry), 'country'),
  ];

  if (input.pricingMode === 'unit') {
    const unit = input.unitLabel?.trim();
    columns.push(column('quantity', unit ? unit : labels.quantity, 'quantity'));
  } else if (input.pricingMode === 'free' || input.pricingMode === 'suggested') {
    columns.push(column('amount', labels.amount, 'amount', true));
  }

  if (input.pricingMode !== 'no_charge' && input.currencies.length > 1) {
    columns.push(
      column(
        'currency',
        labels.currency,
        'choice',
        true,
        input.currencies.map((currency) => ({ value: currency, label: currency })),
      ),
    );
  }

  columns.push(
    column(
      'yurt',
      labels.yurt,
      'choice',
      false,
      input.yurts.map((yurt) => ({ value: yurt.institutionId, label: yurt.label })),
    ),
    column('owner_name', named(owner, labels.partyName), 'text'),
    column('owner_phone', named(owner, labels.partyPhone), 'phone'),
    column('reference_name', named(reference, labels.partyName), 'text'),
    column('reference_phone', named(reference, labels.partyPhone), 'phone'),
    column(
      'payment_method',
      labels.paymentMethod,
      'choice',
      false,
      DONATION_PAYMENT_METHODS.map((method) => ({
        value: method,
        label: labels.paymentMethods[method],
      })),
    ),
    column('payment_reference', labels.paymentReference, 'text'),
    column('note', labels.note, 'text'),
  );

  if (input.mayBackdate) {
    columns.push(
      column('donated_at', labels.donatedAt, 'datetime', input.periodStatus === 'closed'),
    );
  }

  for (const field of input.form.fields) {
    if (field.type === 'file') continue;
    columns.push(fieldColumn(field, labels));
  }

  return { ok: true, columns };
}
